function renderGenM2MFilesServerActionTemplate({
  junctionVar,
  ctxCacheReadImport,
  ctxCamelPlural,
  relPascal,
  ctxSingular,
  ctxSingularText,
  ctxCacheLifeLine,
  relIdCol,
  entityIdCol,
  rel
}: {
  junctionVar: string
  ctxCacheReadImport: string
  ctxCamelPlural: string
  relPascal: string
  ctxSingular: string
  ctxSingularText: string
  ctxCacheLifeLine: string
  relIdCol: string
  entityIdCol: string
  rel: string
}): string {
  return `'use server'

import db from '@admin/db'
import { ${junctionVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
${ctxCacheReadImport}
import { ${ctxCamelPlural}CacheTags } from './types'

export async function get${relPascal}For${ctxSingular}(${ctxSingularText}Id: string): Promise<string[]> {
  'use cache'${ctxCacheLifeLine}
  cacheTag(${ctxCamelPlural}CacheTags.byId(${ctxSingularText}Id))
  cacheTag(${ctxCamelPlural}CacheTags.all)

  try {
    const results = await db
      .select({ ${relIdCol}: ${junctionVar}.${relIdCol} })
      .from(${junctionVar})
      .where(eq(${junctionVar}.${entityIdCol}, ${ctxSingularText}Id))
    return results.map((r) => r.${relIdCol})
  } catch (error) {
    console.error('Error fetching ${rel} for ${ctxSingularText}:', error)
    return []
  }
}
`
}

function renderGenM2MFilesServerActionDetailsTemplate({
  junctionVar,
  ctxTableVar,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  ctxSingular,
  relPascal,
  ctxSingularText,
  rel,
  fName,
  ctxPlural,
  entityIdCol,
  relSingular,
  relIdCol
}: {
  junctionVar: string
  ctxTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  ctxSingular: string
  relPascal: string
  ctxSingularText: string
  rel: string
  fName: string
  ctxPlural: string
  entityIdCol: string
  relSingular: string
  relIdCol: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${junctionVar}, ${ctxTableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctxCamelPlural}CacheTags } from './types'
import {
  build${ctxSingular}VersionSnapshotWithRelationships,
  has${ctxSingular}VersionChanges
} from './helpers'

export async function set${relPascal}For${ctxSingular}(
  ${ctxSingularText}Id: string,
  ${rel}Ids: string[]
): Promise<{ success: boolean; error?: string }> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    await db.transaction(async (tx) => {
      const [previousRow] = await tx
        .select()
        .from(${ctxTableVar})
        .where(eq(${ctxTableVar}.id, ${ctxSingularText}Id))
        .limit(1)

      if (!previousRow) {
        throw new Error('${ctxSingular} not found')
      }

      const previousSnapshot = await build${ctxSingular}VersionSnapshotWithRelationships(
        previousRow as Record<string, unknown>,
        tx
      )
      const nextSnapshot = { ...previousSnapshot, ${fName}: ${rel}Ids }
      const shouldCreateVersion = has${ctxSingular}VersionChanges(previousSnapshot, nextSnapshot)

      if (shouldCreateVersion) {
        await createEntityVersion(tx, {
          entityType: '${ctxPlural}',
          entityId: ${ctxSingularText}Id,
          data: previousSnapshot,
          createdBy: actor.id
        })
      }

      await tx.delete(${junctionVar}).where(eq(${junctionVar}.${entityIdCol}, ${ctxSingularText}Id))
      if (${rel}Ids.length > 0) {
        await tx.insert(${junctionVar}).values(
          ${rel}Ids.map((${relSingular}Id) => ({
            ${entityIdCol}: ${ctxSingularText}Id,
            ${relIdCol}: ${relSingular}Id
          }))
        )
      }
    })
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.byId(${ctxSingularText}Id))
    ${ctxCacheInvalidationFn}(entityVersionsCacheTags.for('${ctxPlural}', ${ctxSingularText}Id))
    return { success: true }
  } catch (error) {
    console.error('Error setting ${rel} for ${ctxSingularText}:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set ${rel}'
    }
  }
}
`
}

export function renderGenM2mFilesRenderer(
  request:
    | {
        kind: 'renderGenM2MFilesServerActionTemplate'
        props: {
          junctionVar: string
          ctxCacheReadImport: string
          ctxCamelPlural: string
          relPascal: string
          ctxSingular: string
          ctxSingularText: string
          ctxCacheLifeLine: string
          relIdCol: string
          entityIdCol: string
          rel: string
        }
      }
    | {
        kind: 'renderGenM2MFilesServerActionDetailsTemplate'
        props: {
          junctionVar: string
          ctxTableVar: string
          ctxCacheInvalidationFn: string
          ctxCamelPlural: string
          ctxSingular: string
          relPascal: string
          ctxSingularText: string
          rel: string
          fName: string
          ctxPlural: string
          entityIdCol: string
          relSingular: string
          relIdCol: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderGenM2MFilesServerActionTemplate':
      return renderGenM2MFilesServerActionTemplate(request.props)
    case 'renderGenM2MFilesServerActionDetailsTemplate':
      return renderGenM2MFilesServerActionDetailsTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
